import type { FeatureFlagService } from "../../services/feature-flags";
import {
  createSpeechSchedule,
  ordersSpeechSegments,
  type BrowserSpeechOutService,
} from "../../services/speech";
import type { VersionedStorageService } from "../../services/versioned-storage";
import {
  DeliveryController,
  cueAtNode,
  deliveryActionForKey,
  deliveryDisplayNodes,
  deliveryDisplayStates,
  deliveryMoments,
  pensDownAfter,
  pensSequenceFor,
  scheduleNodeId,
  spliceAfterNode,
  traceItemForNode,
  loadNotepad,
  saveNotepad,
  type DeliveryAction,
  type DeliverySpeaker,
  type DeliveryState,
} from "./delivery";
import { sharedEventLog } from "../../services/event-log";
import { frenchInSpokenStream } from "./normalization";
import { normalizeOrdersWhenEnabled } from "./normalization-access";
import {
  countWords,
  createLiveRate,
  describeLiveRate,
  type LiveRate,
  type LiveRateState,
} from "../../services/speech";
import { mountConfirmatoryQuestions } from "./questions-ui";
import type { PreflightSession } from "./preflight";
import {
  renderTraceSelection,
  type TraceAssetSelection,
  type TracePaneHandle,
} from "./trace";

export interface DeliverySpeechSetup {
  readonly baseMultiplier: number;
  /** The measured baseline for this voice, where one is stored. */
  readonly baselineWpm?: number | null;
  /** Whether a stored measurement was found. Ruling 64b. */
  readonly measured: boolean;
  readonly speech: BrowserSpeechOutService;
  readonly targetWpm: number;
  readonly voiceId: string;
  readonly voiceLabel: string;
}

export interface OrdersDeliveryOptions {
  readonly fills: Readonly<Record<string, string>>;
  readonly flags: FeatureFlagService;
  /**
   * Called once, when the rate has been measured, so it can be stored for
   * this voice. Ruling 64c.
   */
  readonly onMeasured?: (state: LiveRateState) => void;
  /**
   * Called once, when the run reaches its end.
   *
   * Ruling 58c of 8 October 2026: when the warning order finishes, the Start
   * panel offers "Continue to the orders". Ruling 45 stands — they are two
   * runs — so the panel needs to know the first one is over.
   */
  readonly onComplete?: () => void;
  readonly session: PreflightSession;
  readonly speechSetup: DeliverySpeechSetup;
  readonly storage: VersionedStorageService;
  readonly traceSelection: TraceAssetSelection;
}

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function button(label: string, role: string): HTMLButtonElement {
  const control = element("button", label);
  control.type = "button";
  control.dataset.role = role;
  return control;
}

/**
 * Speaks, and times what it spoke.
 *
 * Ruling 64c: delivery starts at the voice's own speed and measures as it
 * goes, so the multiplier is applied **here** rather than baked into the
 * schedule. The schedule is built once; this is the only place that can
 * change the rate from one item to the next.
 */
function speechSpeaker(
  setup: DeliverySpeechSetup,
  rate: LiveRate,
  onMeasured: (state: LiveRateState) => void,
): DeliverySpeaker {
  return {
    cancel: () => {
      setup.speech.cancel();
    },
    pause: () => {
      setup.speech.pause();
    },
    resume: () => {
      setup.speech.resume();
    },
    speak: async (request) => {
      const words = countWords(request.text);
      const calledAt = performance.now();
      let startedAt: number | undefined;
      const before = rate.state().measured;
      const operation = setup.speech.speak({
        ...request,
        // Ruling 64c: the measured multiplier, from the item after the one
        // that completed the measurement.
        rate: request.rate * rate.state().multiplier,
        voiceId: setup.voiceId,
        onStart: () => {
          startedAt = performance.now();
        },
      });
      if (!(operation instanceof Promise)) {
        throw new Error("Speech output is unavailable in this build.");
      }
      await operation;
      // Ruling 64d: the engine's own start where it gives one, the speak
      // call where it does not, and which was used is recorded.
      const from = startedAt ?? calledAt;
      const elapsedMs = performance.now() - from;
      const state =
        elapsedMs > 0
          ? rate.record({
              elapsedMs,
              source: startedAt === undefined ? "speak-call" : "engine-start",
              words,
            })
          : rate.recordUntimed();
      if (!before && state.measured) onMeasured(state);
    },
  };
}

/**
 * Holds the current chunk in the upper third of the orders pane.
 *
 * Doc 04 section 9.2: "Auto-scroll to keep current chunk on the upper
 * third". Ruling 59e of 8 October 2026, Finding 6 item 5. It used to call
 * `scrollIntoView({ block: "center" })`, which centres it — so half the
 * pane was spent on text already delivered and the lines about to be
 * spoken were pushed off the bottom.
 *
 * `scrollIntoView` has no "upper third" option, so the offset is computed.
 * A line too near the top of the document cannot be a third of the way
 * down, and clamping is what stops it fighting the top of the scroll range.
 */
function scrollToUpperThird(
  pane: HTMLElement,
  line: HTMLElement | undefined,
): void {
  if (line === undefined) return;
  const target = line.offsetTop - pane.clientHeight / 3;
  pane.scrollTo({
    behavior: "smooth",
    top: Math.max(0, target),
  });
}

export function mountOrdersDelivery(
  target: HTMLElement,
  options: OrdersDeliveryOptions,
): () => void {
  if (!options.flags.isEnabled("delivery")) {
    target.replaceChildren(
      element("p", "Orders delivery is disabled in this build."),
    );
    return () => undefined;
  }

  const normalized = normalizeOrdersWhenEnabled(
    options.session.ordersTree,
    options.flags,
    { fills: options.fills },
  );
  if (!normalized.ok) {
    target.replaceChildren(element("p", normalized.error.message));
    return () => undefined;
  }

  // Ruling 81, 17 October 2026 (a): an English document carries no French.
  // Checked where the spoken stream comes into existence, and recorded rather
  // than refused: a refusal would stop a run over one word, and Ruling 81
  // gives no authority to stop delivery. Ruling 50: the marker and the
  // segment's number, never the segment.
  const french = frenchInSpokenStream(normalized.value.spokenTextStream);
  if (french.length > 0) {
    sharedEventLog().append({
      module: "orders-reader",
      action: "french-in-english-stream",
      message:
        "Ruling 81: French reached the English delivery stream. The document needs the English card's wording for that item.",
      detail: {
        findings: french.length,
        firstKind: french[0]?.kind ?? "",
        firstMarker: french[0]?.marker ?? "",
        firstSegment: french[0]?.index ?? -1,
      },
    });
  }

  // Ruling 59. Doc 03 section 6.2: the detailed scheme of manoeuvre is not
  // in the document, so there is no node to deliver it at. The interlude
  // supplies those nodes, spliced in after the last CONOPS item a phase
  // binds to, and goes through the ordinary schedule and the ordinary
  // display from there. With the eyesUp flag off it is empty and nothing
  // below it changes.
  const pens = pensSequenceFor(
    options.session.ordersTree,
    options.traceSelection.sidecar?.phases ?? [],
    options.flags,
  );
  const schedule = createSpeechSchedule(
    spliceAfterNode(
      ordersSpeechSegments(normalized.value),
      pens.segments,
      pens.afterNodeId,
      ({ id }) => scheduleNodeId(id),
    ),
    // 1, because the multiplier is applied by the speaker now: the
    // schedule is fixed once and the rate is not.
    { baseMultiplier: 1 },
    Math.random,
  );
  const nodes = spliceAfterNode(
    deliveryDisplayNodes(options.session.ordersTree),
    pens.displayNodes,
    pens.afterNodeId,
    ({ id }) => id,
  );

  const frame = element("section");
  frame.className = "delivery-frame";
  frame.dataset.role = "orders-delivery";

  const header = element("header");
  header.className = "delivery-header";
  const title = element(
    "h2",
    options.session.ordersTree.title ?? "Orders delivery",
  );
  const position = element("p", "Ready");
  position.dataset.role = "delivery-position";
  header.append(title, position);

  // R1 Step 13. Doc 04 section 1.2 gives EYES_UP a banner and a single tone;
  // the resume is a silent visual transition with no tone and no spoken call.
  const cueBanner = element("p", "Pens down, eyes up.");
  cueBanner.className = "cue-banner";
  cueBanner.dataset.role = "cue-banner";
  cueBanner.hidden = true;
  // Ruling 59a: EYES_UP fires after the last phase-bound item, which means
  // on reaching the interlude's own call node. The stored or detected
  // position is overridden only where an interlude exists; with no
  // interlude the session's position stands, as it did before.
  const cueBindings = options.session.cues.map(({ name, nodeId }) => ({
    name,
    nodeId:
      name === "EYES_UP" && pens.callNodeId !== null ? pens.callNodeId : nodeId,
  }));

  const amplitude = element("div");
  amplitude.className = "amplitude-bar";
  amplitude.dataset.role = "amplitude";
  amplitude.setAttribute("aria-hidden", "true");

  const workspace = element("div");
  workspace.className = "delivery-workspace";
  const ordersPane = element("section");
  ordersPane.className = "orders-pane";
  ordersPane.setAttribute("aria-label", "Orders text");
  const textElements = new Map<string, HTMLElement>();
  for (const node of nodes) {
    const tag =
      node.kind === "paragraph-heading"
        ? "h3"
        : node.kind === "subparagraph-heading"
          ? "h4"
          : "p";
    const line = element(tag, node.text);
    line.dataset.nodeId = node.id;
    line.className = `delivery-text ${node.kind}`;
    // Ruling 59. These lines are not card text: the call is doc 03 section
    // 6.1's locked wording and the phase labels come from the sidecar. Marked
    // so the pane can show them as what they are, because the orders pane is
    // otherwise source-faithful and passing them off as card text would be a
    // quiet lie in the one pane he reads along with.
    if (pens.displayNodes.some(({ id }) => id === node.id)) {
      line.dataset.interlude = "true";
    }
    textElements.set(node.id, line);
    ordersPane.append(line);
  }
  const tracePane = element("aside");
  tracePane.className = "trace-pane";
  const traceContent = element("div");
  traceContent.dataset.role = "delivery-trace";
  tracePane.append(element("h3", "Trace"), traceContent);
  const trace: TracePaneHandle = renderTraceSelection(
    traceContent,
    options.traceSelection,
    options.flags,
    options.speechSetup.targetWpm,
  );
  workspace.append(ordersPane, tracePane);

  // Ruling 60: revealed only when the run finishes, so it cannot be
  // answered from the text still on screen.
  const questionsPhase = element("section");
  questionsPhase.className = "questions-phase";
  questionsPhase.dataset.role = "questions-phase";
  questionsPhase.hidden = true;

  const notepadDrawer = element("details");
  notepadDrawer.className = "notepad-drawer";
  notepadDrawer.open = true;
  notepadDrawer.append(element("summary", "Notepad"));
  const notepad = element("textarea");
  notepad.dataset.role = "notepad";
  notepad.setAttribute("aria-label", "Notepad");
  const notepadStatus = element("p");
  notepadStatus.className = "storage-status";
  const loadedNotepad = loadNotepad(options.storage, options.session.fileHash);
  notepad.value = loadedNotepad.text;
  notepadStatus.textContent = loadedNotepad.issue ?? "Saved on this device.";
  notepad.addEventListener("input", () => {
    const issue = saveNotepad(
      options.storage,
      options.session.fileHash,
      notepad.value,
    );
    notepadStatus.textContent = issue ?? "Saved on this device.";
    notepadStatus.dataset.status = issue === null ? "saved" : "error";
  });
  notepadDrawer.append(notepad, notepadStatus);

  const transport = element("footer");
  transport.className = "delivery-transport";
  const previous = button("Previous", "delivery-previous");
  const playPause = button("Play", "delivery-play-pause");
  const stop = button("Stop", "delivery-stop");
  const next = button("Next", "delivery-next");
  const sayAgain = button("Say again", "delivery-say-again");
  const slower = button("-5 wpm", "delivery-slower");
  const faster = button("+5 wpm", "delivery-faster");
  const rate = element(
    "output",
    `${String(options.speechSetup.targetWpm)} wpm`,
  );
  rate.dataset.role = "delivery-rate";

  // Ruling 64, 12 October 2026: the rate is measured during delivery, so
  // until it is the screen says so and shows no figure. A wpm number nobody
  // measured is worse than no number.
  const liveRate = createLiveRate(options.speechSetup.targetWpm);
  const rateLine = element("p");
  rateLine.dataset.role = "delivery-rate-state";
  rateLine.textContent = options.speechSetup.measured
    ? describeLiveRate(
        {
          ...liveRate.state(),
          measured: true,
          baselineWpm: options.speechSetup.baselineWpm ?? null,
        },
        options.speechSetup.targetWpm,
      )
    : describeLiveRate(liveRate.state(), options.speechSetup.targetWpm);
  const voice = element("span", `Voice: ${options.speechSetup.voiceLabel}`);
  voice.className = "delivery-voice";
  const jumps = element("div");
  jumps.className = "paragraph-jumps";
  jumps.setAttribute("aria-label", "Jump to paragraph");
  for (let paragraph = 1; paragraph <= 5; paragraph += 1) {
    const jump = button(
      String(paragraph),
      `delivery-jump-${String(paragraph)}`,
    );
    jump.title = `Jump to paragraph ${String(paragraph)}`;
    jump.addEventListener("click", () => {
      controller.seek({ type: "JUMP", paragraph });
    });
    jumps.append(jump);
  }
  transport.append(
    previous,
    playPause,
    stop,
    next,
    sayAgain,
    slower,
    rate,
    faster,
    jumps,
    voice,
  );

  const error = element("p");
  error.className = "delivery-error";
  error.dataset.role = "delivery-error";
  error.hidden = true;

  frame.append(
    header,
    cueBanner,
    amplitude,
    workspace,
    notepadDrawer,
    transport,
    rateLine,
    error,
    // Last, and hidden until the run finishes.
    questionsPhase,
  );
  target.replaceChildren(frame);

  let currentNodeId: string | null = null;
  // Once only: the render runs on every tick and the panel must be offered
  // the next document once, not on each frame after the run ends.
  let announcedComplete = false;
  let pensDown = false;
  // Ruling 84. The one log, as the intake uses, so the timeline and the
  // operational events are in the same export.
  const diagnostics = sharedEventLog();
  let previousState: DeliveryState | null = null;

  const playCueTone = (): void => {
    try {
      const audio = new AudioContext();
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.frequency.value = 880;
      gain.gain.value = 0.05;
      oscillator.connect(gain).connect(audio.destination);
      oscillator.start();
      oscillator.stop(audio.currentTime + 0.18);
      oscillator.addEventListener("ended", () => void audio.close());
    } catch (failure: unknown) {
      // Never silent. Brave farbles Web Audio, so the tone can fail while the
      // banner is correct; say so rather than swallowing it.
      error.hidden = false;
      error.textContent =
        `The eyes-up tone could not play, but the banner is shown. ${failure instanceof Error ? failure.message : ""}`.trim();
    }
  };
  const renderState = (state: DeliveryState): void => {
    // Ruling 84, first thing: start, each heading, pause, resume, stop, end.
    // Before any rendering, so a moment is recorded even if a later line of
    // this function throws.
    for (const { detail, name } of deliveryMoments(
      previousState,
      state,
      schedule,
    )) {
      diagnostics.moment(name, detail);
    }
    previousState = state;
    amplitude.dataset.status = state.status;
    playPause.textContent = state.status === "playing" ? "Pause" : "Play";
    rate.value = `${String(state.targetWpm)} wpm`;
    rate.textContent = rate.value;
    const states = deliveryDisplayStates(nodes, schedule, state);
    for (const display of states) {
      const line = textElements.get(display.id);
      if (line !== undefined) line.dataset.state = display.state;
    }
    const current = states.find(
      ({ state: textState }) => textState === "current",
    );
    position.textContent =
      state.status === "complete"
        ? "Complete"
        : (current?.text ?? state.status.toUpperCase());
    if (state.status === "complete" && !announcedComplete) {
      announcedComplete = true;
      // Ruling 60, 8 October 2026: the confirmatory questions run **after
      // COMDSIGS completes**, which is doc 04 section 12 as written. They
      // used to sit in pre-flight, where they asked him about orders he had
      // not heard yet — so the phase that checks what he took down ran
      // before there was anything to take down.
      const asked = mountConfirmatoryQuestions(questionsPhase, {
        fileHash: options.session.fileHash,
        flags: options.flags,
        // Ruling 84: questions started and finished. The phase reports its
        // own two moments, because only it knows when the last one is
        // answered. It reports counts; **no answer of his goes in.**
        onMoment: (name, detail) => {
          diagnostics.moment(name, detail);
        },
        storage: options.storage,
        tree: options.session.ordersTree,
      });
      // Hard rule 7: with the flag false nothing mounted, so the section
      // stays hidden and nothing of the phase is on screen.
      questionsPhase.hidden = !asked;
      options.onComplete?.();
    }
    if (current !== undefined && current.id !== currentNodeId) {
      currentNodeId = current.id;
      // The one call. The trace pane decides whether that means anything:
      // with its feature off it holds an inert handle and this is a no-op.
      // Ruling 59b and 59e: the walk runs while the phase's detail is
      // spoken, in the interlude, and not at the phase name in the general
      // scheme where he is still writing.
      trace.deliveryReached(traceItemForNode(pens, current.id));
      const cue = cueAtNode(cueBindings, current.id);
      const nextPensDown = pensDownAfter(pensDown, cue);
      if (nextPensDown !== pensDown) {
        pensDown = nextPensDown;
        cueBanner.hidden = !pensDown;
        frame.dataset.pensDown = String(pensDown);
        // Ruling 84: the trace view shown and hidden. In this build it is
        // shown by coming forward at pens down and hidden by going back at
        // the main effort — there is no toggle yet. When one arrives it
        // records these same two moments and nothing else changes.
        diagnostics.moment(pensDown ? "trace-shown" : "trace-hidden", {
          by: "pens-down",
        });
        // EYES_UP carries a single tone. The resume is silent by Q16.
        if (cue === "EYES_UP") playCueTone();
      }
      scrollToUpperThird(ordersPane, textElements.get(current.id));
    }
  };

  const controller = new DeliveryController({
    schedule,
    targetWpm: options.speechSetup.targetWpm,
    speaker: speechSpeaker(options.speechSetup, liveRate, (state) => {
      rateLine.textContent = describeLiveRate(
        state,
        options.speechSetup.targetWpm,
      );
      options.onMeasured?.(state);
    }),
    clock: {
      wait: async (delayMs) =>
        await new Promise<void>((resolve) =>
          window.setTimeout(resolve, delayMs),
        ),
    },
    marker: () => Promise.resolve(),
    onError: (failure) => {
      error.hidden = false;
      error.textContent =
        failure instanceof Error ? failure.message : "Speech delivery failed.";
    },
    onState: renderState,
  });

  const seek = (
    action: Extract<DeliveryAction, { readonly type: "NEXT" | "PREVIOUS" }>,
  ): void => controller.seek(action);
  playPause.addEventListener("click", () => {
    if (controller.state().status === "playing") controller.pause();
    else controller.play();
  });
  stop.addEventListener("click", () => controller.stop());
  previous.addEventListener("click", () => seek({ type: "PREVIOUS" }));
  next.addEventListener("click", () => seek({ type: "NEXT" }));
  sayAgain.addEventListener("click", () => void controller.sayAgain());
  slower.addEventListener("click", () => controller.nudge(-5));
  faster.addEventListener("click", () => controller.nudge(5));

  const keydown = (event: KeyboardEvent): void => {
    const eventTarget = event.target;
    const isTyping =
      eventTarget instanceof HTMLInputElement ||
      eventTarget instanceof HTMLTextAreaElement ||
      eventTarget instanceof HTMLSelectElement ||
      (eventTarget instanceof HTMLElement && eventTarget.isContentEditable);
    if (isTyping && event.key !== "Escape") return;
    const action = deliveryActionForKey(event.key);
    if (action === null) return;
    event.preventDefault();
    if (action === "SAY_AGAIN") {
      void controller.sayAgain();
    } else if (action.type === "PLAY") {
      if (controller.state().status === "playing") controller.pause();
      else controller.play();
    } else if (action.type === "STOP") {
      controller.stop();
    } else if (action.type === "NUDGE_RATE") {
      controller.nudge(action.delta);
    } else {
      controller.seek(action);
    }
  };
  document.addEventListener("keydown", keydown);

  return () => {
    document.removeEventListener("keydown", keydown);
    controller.stop();
  };
}
